/**
 * Session Tracking Service
 *
 * Comprehensive session management for tracking user logins across devices.
 * Creates session entries on login, tracks activity, and supports admin oversight.
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  limit,
} from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import { generateSecureId } from '@/utils/random';
import { safeLocalStorage } from '@/services/utils/safeStorage';

type DeviceType = 'desktop' | 'mobile' | 'tablet' | 'unknown';

// Session document structure
export interface SessionData {
  id: string;
  userId: string;
  // Device information
  userAgent: string;
  platform: string;
  browser: string;
  deviceType: DeviceType;
  // Timestamps
  createdAt: Timestamp;
  lastActiveAt: Timestamp;
  // Additional metadata
  ipAddress?: string; // Not available client-side, but can be set by cloud functions
  location?: string;
  // Status
  isActive: boolean;
}

function detectDeviceType(ua: string): DeviceType {
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  if (/windows|macintosh|linux|cros/i.test(ua)) return 'desktop';
  return 'unknown';
}

function detectPlatform(ua: string): string {
  const checks: [RegExp, string][] = [
    [/windows/i, 'Windows'],
    [/macintosh|mac os/i, 'macOS'],
    [/linux/i, 'Linux'],
    [/android/i, 'Android'],
    [/iphone|ipad|ipod/i, 'iOS'],
    [/cros/i, 'Chrome OS'],
  ];
  for (const [re, name] of checks) {
    if (re.test(ua)) return name;
  }
  return 'Unknown';
}

function detectBrowser(ua: string): string {
  if (/edg/i.test(ua)) return 'Edge';
  if (/chrome/i.test(ua) && !/edg/i.test(ua)) return 'Chrome';
  if (/firefox/i.test(ua)) return 'Firefox';
  if (/safari/i.test(ua) && !/chrome/i.test(ua)) return 'Safari';
  if (/opera|opr/i.test(ua)) return 'Opera';
  if (/msie|trident/i.test(ua)) return 'Internet Explorer';
  return 'Unknown';
}

function parseUserAgent(userAgent: string): {
  browser: string;
  platform: string;
  deviceType: DeviceType;
} {
  const ua = userAgent.toLowerCase();
  return {
    browser: detectBrowser(ua),
    platform: detectPlatform(ua),
    deviceType: detectDeviceType(ua),
  };
}

/**
 * Generate a device fingerprint based on browser characteristics.
 * This helps identify the same browser/device across sessions.
 */
function getDeviceFingerprint(): string {
  if (typeof navigator === 'undefined') return 'unknown';

  const userAgent = navigator.userAgent;
  const { browser, platform, deviceType } = parseUserAgent(userAgent);

  // Include screen info for uniqueness but not so specific it changes on window resize
  const screenInfo = typeof screen !== 'undefined' ? `${screen.width}x${screen.height}` : 'unknown';

  // Include timezone as additional signal
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';

  return `${browser}-${platform}-${deviceType}-${screenInfo}-${timezone}`;
}

/**
 * Generate a unique session ID
 */
function generateSessionId(): string {
  return generateSecureId(`${Date.now().toString(36)}-`);
}

// Storage key constants
const SESSION_ID_KEY = 'currentSessionId';
const DEVICE_FINGERPRINT_KEY = 'deviceFingerprint';

/**
 * Get the current session ID from localStorage (persists across browser restarts)
 * Uses safeLocalStorage for Safari Private Mode compatibility.
 */
export function getCurrentSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  return safeLocalStorage.getItem(SESSION_ID_KEY);
}

/**
 * Store the current session ID in localStorage
 * Uses safeLocalStorage for Safari Private Mode compatibility.
 */
function setCurrentSessionId(sessionId: string): void {
  if (typeof window === 'undefined') return;
  safeLocalStorage.setItem(SESSION_ID_KEY, sessionId);
  // Also store the device fingerprint so we can match later
  safeLocalStorage.setItem(DEVICE_FINGERPRINT_KEY, getDeviceFingerprint());
}

/**
 * Clear the current session ID from localStorage
 * Uses safeLocalStorage for Safari Private Mode compatibility.
 */
export function clearCurrentSessionId(): void {
  if (typeof window === 'undefined') return;
  safeLocalStorage.removeItem(SESSION_ID_KEY);
}

/**
 * Find an existing session for the current device fingerprint.
 * Returns the session ID if found and valid, null otherwise.
 */
async function findExistingSessionForDevice(userId: string): Promise<string | null> {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown';
  const { browser, platform, deviceType } = parseUserAgent(userAgent);

  try {
    const sessionsRef = collection(db, 'artifacts', appId, 'users', userId, 'sessions');
    const snapshot = await getDocs(sessionsRef);

    // Find a session that matches this device's characteristics
    for (const docSnap of snapshot.docs) {
      const session = docSnap.data() as SessionData;

      // Check if session matches current device (browser, platform, deviceType)
      // and is still considered active (updated within last 24 hours)
      if (
        session.browser === browser &&
        session.platform === platform &&
        session.deviceType === deviceType &&
        session.isActive
      ) {
        // Check if session was active recently (within 24 hours)
        const lastActiveAt = session.lastActiveAt;
        if (lastActiveAt) {
          const lastActiveTime = lastActiveAt.toMillis();
          const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;

          if (lastActiveTime > twentyFourHoursAgo) {
            return docSnap.id;
          }
        }
      }
    }
  } catch (error) {
    console.warn('Error finding existing session:', error);
  }

  return null;
}

/**
 * Session creation lock to prevent concurrent session creation.
 * Stores the in-flight promise so concurrent calls can share the result.
 */
let sessionCreationPromise: Promise<string> | null = null;

/**
 * Create a new session when user logs in, or reuse an existing one for the same device.
 *
 * Fixed race condition: Uses a promise lock to prevent multiple concurrent calls
 * from creating duplicate sessions. Concurrent calls will share the same promise.
 */
export async function createSession(userId: string): Promise<string> {
  // If a session creation is already in progress, return that promise
  // This prevents duplicate sessions when createSession is called concurrently
  if (sessionCreationPromise) {
    return sessionCreationPromise;
  }

  // Create a new promise and store it as the lock
  sessionCreationPromise = (async () => {
    try {
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown';
      const { browser, platform, deviceType } = parseUserAgent(userAgent);

      // First, check if there's an existing session for this device that we can reuse
      const existingSessionId = await findExistingSessionForDevice(userId);
      if (existingSessionId) {
        // Update the existing session's activity instead of creating a new one
        try {
          const sessionRef = doc(
            db,
            'artifacts',
            appId,
            'users',
            userId,
            'sessions',
            existingSessionId
          );
          await updateDoc(sessionRef, {
            lastActiveAt: serverTimestamp(),
            isActive: true,
            // Update user agent in case it changed slightly
            userAgent,
          });
          setCurrentSessionId(existingSessionId);
          return existingSessionId;
        } catch (error) {
          // Session might have been deleted, fall through to create a new one
          console.warn('Could not reuse existing session, creating new one:', error);
        }
      }

      // Create a new session
      const sessionId = generateSessionId();
      const sessionsRef = collection(db, 'artifacts', appId, 'users', userId, 'sessions');
      const sessionDocRef = doc(sessionsRef, sessionId);

      const sessionData = {
        userId,
        userAgent,
        platform,
        browser,
        deviceType,
        createdAt: serverTimestamp(),
        lastActiveAt: serverTimestamp(),
        isActive: true,
      };

      await setDoc(sessionDocRef, sessionData);
      setCurrentSessionId(sessionId);

      return sessionId;
    } finally {
      // Clear the lock after completion (success or error)
      sessionCreationPromise = null;
    }
  })();

  return sessionCreationPromise;
}

/**
 * Update session activity (heartbeat)
 */
export async function updateSessionActivity(userId: string): Promise<void> {
  const sessionId = getCurrentSessionId();
  if (!sessionId) return;

  try {
    const sessionRef = doc(db, 'artifacts', appId, 'users', userId, 'sessions', sessionId);
    await updateDoc(sessionRef, {
      lastActiveAt: serverTimestamp(),
      isActive: true,
    });
  } catch (error) {
    // Log the error but don't clear the session ID
    // The session might be temporarily unavailable (network issues)
    // or it was revoked - the revocation subscription will handle logout
    console.warn('Could not update session activity:', error);
    // Note: We intentionally don't clear the session ID here to prevent
    // duplicate sessions being created on temporary network failures.
    // If the session was truly revoked, the subscribeToCurrentSession
    // listener will handle the logout.
  }
}

/**
 * End the current session (logout)
 */
export async function endSession(userId: string): Promise<void> {
  const sessionId = getCurrentSessionId();
  if (!sessionId) return;

  try {
    const sessionRef = doc(db, 'artifacts', appId, 'users', userId, 'sessions', sessionId);
    await updateDoc(sessionRef, {
      isActive: false,
      endedAt: serverTimestamp(),
    });
  } catch (error) {
    console.warn('Could not end session:', error);
  } finally {
    clearCurrentSessionId();
  }
}

/**
 * Delete a specific session (revoke)
 */
export async function deleteSession(userId: string, sessionId: string): Promise<void> {
  const sessionRef = doc(db, 'artifacts', appId, 'users', userId, 'sessions', sessionId);
  await deleteDoc(sessionRef);

  // If it's the current session, clear it
  if (getCurrentSessionId() === sessionId) {
    clearCurrentSessionId();
  }
}

/**
 * Delete all sessions for a user
 */
export async function deleteAllSessions(userId: string): Promise<number> {
  const sessionsRef = collection(db, 'artifacts', appId, 'users', userId, 'sessions');
  const snapshot = await getDocs(query(sessionsRef, limit(200)));

  if (snapshot.empty) return 0;

  const deletePromises = snapshot.docs.map((doc) => deleteDoc(doc.ref));
  await Promise.all(deletePromises);

  clearCurrentSessionId();
  return snapshot.size;
}

/**
 * Get all sessions for a user
 */
export async function getUserSessions(userId: string): Promise<SessionData[]> {
  const sessionsRef = collection(db, 'artifacts', appId, 'users', userId, 'sessions');
  const q = query(sessionsRef, orderBy('lastActiveAt', 'desc'), limit(200));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as SessionData[];
}

/**
 * Subscribe to user sessions in real-time
 */
export function subscribeToUserSessions(
  userId: string,
  callback: (sessions: SessionData[]) => void
): () => void {
  const sessionsRef = collection(db, 'artifacts', appId, 'users', userId, 'sessions');
  const q = query(sessionsRef, orderBy('lastActiveAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const sessions = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as SessionData[];
    callback(sessions);
  });
}

/**
 * Subscribe to current session status for real-time revocation detection.
 * Calls onRevoked callback when the session is explicitly deleted by admin.
 *
 * This is conservative - only triggers logout when:
 * 1. The session was confirmed to exist at least once
 * 2. AND it subsequently stops existing (deleted)
 *
 * Does NOT trigger logout on:
 * - First snapshot being empty (race condition)
 * - Permission errors (could be temporary)
 * - Any errors at all (let other validation handle it)
 */
export function subscribeToCurrentSession(userId: string, onRevoked: () => void): () => void {
  const sessionId = getCurrentSessionId();
  if (!sessionId) {
    // No session to monitor
    return () => {};
  }

  const sessionRef = doc(db, 'artifacts', appId, 'users', userId, 'sessions', sessionId);

  // Only trigger revocation if we've confirmed the session existed first
  let confirmedExists = false;

  return onSnapshot(
    sessionRef,
    (snapshot) => {
      if (snapshot.exists()) {
        // Session exists - mark as confirmed
        if (!confirmedExists) {
          confirmedExists = true;
        }
      } else if (confirmedExists) {
        // Session was confirmed to exist but now doesn't - it was revoked
        console.warn('Session was revoked by admin - triggering logout');
        clearCurrentSessionId();
        onRevoked();
      } else {
        // Session doesn't exist but we never confirmed it did
        // This is likely a race condition - ignore it
      }
    },
    (error) => {
      // Never treat errors as revocation - too risky for false positives
      // Real revocations will be caught by the snapshot handler when the doc disappears
      console.warn('Session monitoring error (ignoring):', error);
    }
  );
}

/**
 * Check if the current session is still valid
 */
export async function isCurrentSessionValid(userId: string): Promise<boolean> {
  const sessionId = getCurrentSessionId();
  if (!sessionId) return false;

  try {
    const sessionRef = doc(db, 'artifacts', appId, 'users', userId, 'sessions', sessionId);
    const snapshot = await getDoc(sessionRef);
    return snapshot.exists();
  } catch (error) {
    console.warn('Could not check session validity:', error);
    return false;
  }
}

/**
 * Ensure a valid session exists for the user.
 * If a stored session ID exists but is invalid, clears it and creates a new session.
 * If no session ID exists, creates a new session.
 * If a valid session exists, returns it.
 */
export async function ensureValidSession(userId: string): Promise<string> {
  const existingId = getCurrentSessionId();

  if (existingId) {
    // Validate the session still exists in Firestore
    const isValid = await isCurrentSessionValid(userId);
    if (isValid) {
      // Session is valid, update its activity
      await updateSessionActivity(userId);
      return existingId;
    }

    // Session ID exists locally but not in Firestore - clear it
    clearCurrentSessionId();
  }

  // Create a new session (this will also check for existing device sessions)
  return createSession(userId);
}

// ============================================
// SESSION ACTIVITY TRACKER (Heartbeat)
// ============================================

/**
 * Heartbeat state encapsulated in an object to prevent race conditions
 * when multiple calls to startSessionHeartbeat occur.
 */
interface HeartbeatState {
  interval: ReturnType<typeof setInterval> | null;
  lastActivityTime: number;
  activityHandler: (() => void) | null;
  userId: string | null;
}

const heartbeatState: HeartbeatState = {
  interval: null,
  lastActivityTime: Date.now(),
  activityHandler: null,
  userId: null,
};

/**
 * Start the session heartbeat to track activity
 * Updates every 5 minutes if there's been activity
 *
 * Fixed race condition: Now stops any existing heartbeat before starting a new one,
 * preventing multiple intervals and event listener leaks.
 */
export function startSessionHeartbeat(userId: string): void {
  // IMPORTANT: Stop any existing heartbeat first to prevent duplicate intervals
  // This fixes the race condition where concurrent calls could create multiple intervals
  stopSessionHeartbeat();

  const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes
  const ACTIVITY_THRESHOLD = 60 * 1000; // 1 minute

  // Store the userId for later reference
  heartbeatState.userId = userId;

  // Track user activity - store reference so we can remove it later
  heartbeatState.activityHandler = () => {
    heartbeatState.lastActivityTime = Date.now();
  };

  // Add activity listeners
  if (typeof window !== 'undefined') {
    window.addEventListener('mousemove', heartbeatState.activityHandler, { passive: true });
    window.addEventListener('keydown', heartbeatState.activityHandler, { passive: true });
    window.addEventListener('touchstart', heartbeatState.activityHandler, { passive: true });
    window.addEventListener('scroll', heartbeatState.activityHandler, { passive: true });
  }

  // Start heartbeat interval
  heartbeatState.interval = setInterval(async () => {
    // Verify we still have a valid userId (could be cleared by stopSessionHeartbeat)
    const currentUserId = heartbeatState.userId;
    if (!currentUserId) return;

    // Only update if there's been recent activity
    const timeSinceActivity = Date.now() - heartbeatState.lastActivityTime;
    if (timeSinceActivity < HEARTBEAT_INTERVAL + ACTIVITY_THRESHOLD) {
      try {
        await updateSessionActivity(currentUserId);
      } catch (error) {
        // Don't let heartbeat errors crash the interval
        console.warn('Heartbeat update failed:', error);
      }
    }
  }, HEARTBEAT_INTERVAL);

  // Initial update (fire-and-forget with error handling)
  updateSessionActivity(userId).catch((error) => {
    console.warn('Initial heartbeat update failed:', error);
  });
}

/**
 * Stop the session heartbeat
 * Cleans up all resources: interval, event listeners, and state
 */
export function stopSessionHeartbeat(): void {
  // Clear interval first
  if (heartbeatState.interval) {
    clearInterval(heartbeatState.interval);
    heartbeatState.interval = null;
  }

  // Remove activity listeners using the stored handler reference
  if (typeof window !== 'undefined' && heartbeatState.activityHandler) {
    window.removeEventListener('mousemove', heartbeatState.activityHandler);
    window.removeEventListener('keydown', heartbeatState.activityHandler);
    window.removeEventListener('touchstart', heartbeatState.activityHandler);
    window.removeEventListener('scroll', heartbeatState.activityHandler);
  }

  // Reset all state
  heartbeatState.activityHandler = null;
  heartbeatState.userId = null;
  heartbeatState.lastActivityTime = Date.now();
}
